import { useEffect, useState, useMemo, useCallback } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetDealerStockListQuery, useGetUserParamsQuery } from '../../../../api';
import { useCreateReturnRequestMutation, useSendToRegionalManagerMutation, useCancelReturnRequestMutation, useApproveReturnRequestMutation, useRejectReturnRequestMutation } from '../../../../api/endpoints/dealerPartsReturnProcess';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { toastService } from '../../../../utils/toastService';
import { matchModeToOperator } from '../../../../utils/filterUtils';

export const backendColumnMap = {
    returnRequestId: 'ReturnRequestId',
    dealerInfo: 'DealerInfo',
    partCode: 'PartCode',
    partDesc: 'PartDesc',
    uom: 'Uom',
    quantity: 'Quantity',
    warehouseName: 'WarehouseName',
    isActive: 'IsActive',
    invoiceNo: 'invoiceNo'
};

export const TEXT_FIELDS = ['partCode', 'partDesc', 'invoiceNo'];
export const NUMERIC_FIELDS = ['returnRequestId', 'quantity'];
export const DROPDOWN_FIELDS = ['dealerInfo', 'uom', 'warehouseName', 'isActive'];
export const STATIC_DROPDOWN_FIELDS = ['isActive'];

const FILTER_OPTIONS_COLUMNS = ['DealerInfo', 'Uom', 'WarehouseName'];

export const DOMESTIC_TYPE = { DOMESTIC: 1, FOREIGN: 2 };

const OPERATOR_EQUALS = 5;

const INITIAL_LAZY_PARAMS = { first: 0, rows: 10, page: 1 };

const INITIAL_FILTER_STATE = {
    ...TEXT_FIELDS.reduce((acc, field) => {
        acc[field] = null;
        acc[`${field}MatchMode`] = null;
        return acc;
    }, {}),
    ...NUMERIC_FIELDS.reduce((acc, field) => {
        acc[field] = null;
        acc[`${field}MatchMode`] = null;
        return acc;
    }, {}),
    ...DROPDOWN_FIELDS.reduce((acc, field) => {
        acc[field] = null;
        return acc;
    }, {})
};

const getStatusByActiveIndex = (index) => {
    switch (index) {
        case 0:
            return 10;
        case 1:
            return 20;
        case 2:
            return 30;
        case 3:
            return 40;
        default:
            return null;
    }
};

const getOperatorValue = (matchMode, fallbackMatchMode) => matchModeToOperator[matchMode] ?? matchModeToOperator[fallbackMatchMode];

const hasFilterValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    TEXT_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (!hasFilterValue(value)) return;
        filters.push({
            column: backendColumnMap[field],
            operator: getOperatorValue(filtersState?.[`${field}MatchMode`], FilterMatchMode.CONTAINS),
            value: String(value).trim()
        });
    });

    NUMERIC_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (!hasFilterValue(value)) return;
        filters.push({
            column: backendColumnMap[field],
            operator: getOperatorValue(filtersState?.[`${field}MatchMode`], FilterMatchMode.EQUALS),
            value: String(value).trim()
        });
    });

    DROPDOWN_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (!hasFilterValue(value)) return;
        filters.push({
            column: backendColumnMap[field],
            operator: OPERATOR_EQUALS,
            value: String(value)
        });
    });

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = backendColumnMap[field];
    if (!column) return null;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

const useDealerPartsReturnProcessData = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const [createReturnRequest, { isLoading: isSubmitting }] = useCreateReturnRequestMutation();
    const [sendToRegionalManager, { isLoading: isSendingToRM }] = useSendToRegionalManagerMutation();
    const [cancelReturnRequest, { isLoading: isCancellingRequest }] = useCancelReturnRequestMutation();
    const [approveReturnRequest, { isLoading: isApproving }] = useApproveReturnRequestMutation();
    const [rejectReturnRequest, { isLoading: isRejecting }] = useRejectReturnRequestMutation();

    const [activeIndex, setActiveIndex] = useState(() => {
        try {
            const savedIndex = localStorage.getItem('dealerPartsReturnProcessActiveTab');
            return savedIndex ? parseInt(savedIndex, 10) : 0;
        } catch (error) {
            console.error('Error reading activeIndex from localStorage:', error);
            return 0;
        }
    });

    const [lazyParams, setLazyParams] = useState(INITIAL_LAZY_PARAMS);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [domesticType, setDomesticType] = useState(null);
    const [isDomesticTypeInitialized, setIsDomesticTypeInitialized] = useState(false);
    const [filterOptions, setFilterOptions] = useState(null);
    const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);

    const { data: userParams, isSuccess: isUserParamsSuccess, isError: isUserParamsError } = useGetUserParamsQuery();
    const isDomestic = typeof userParams?.isDomestic === 'boolean' ? userParams.isDomestic : undefined;

    useEffect(() => {
        if (isDomesticTypeInitialized || (!isUserParamsSuccess && !isUserParamsError)) return;
        if (isDomestic === true) setDomesticType(DOMESTIC_TYPE.DOMESTIC);
        else if (isDomestic === false) setDomesticType(DOMESTIC_TYPE.FOREIGN);
        setIsDomesticTypeInitialized(true);
    }, [isDomestic, isUserParamsSuccess, isUserParamsError, isDomesticTypeInitialized]);

    const queryParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            tabStatus: getStatusByActiveIndex(activeIndex),
            domesticType,
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            languageId: getCurrentLanguageId(),
            filters: filters.length > 0 ? filters : null,
            sorts: sorts && sorts.length > 0 ? sorts : null,
            includeFilterOptions: !filterOptionsLoaded,
            filterOptionsColumns: !filterOptionsLoaded ? FILTER_OPTIONS_COLUMNS : null
        };
    }, [activeIndex, domesticType, lazyParams.page, lazyParams.rows, activeFilters, sortField, sortOrder, filterOptionsLoaded, getCurrentLanguageId]);

    const exportParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            tabStatus: getStatusByActiveIndex(activeIndex),
            domesticType,
            languageId: getCurrentLanguageId(),
            filters: filters.length > 0 ? filters : null,
            sorts: sorts && sorts.length > 0 ? sorts : null
        };
    }, [activeIndex, domesticType, activeFilters, sortField, sortOrder, getCurrentLanguageId]);

    const {
        data: apiData,
        currentData,
        isLoading,
        isFetching,
        error,
        refetch
    } = useGetDealerStockListQuery(queryParams, {
        skip: !getCurrentLanguageId() || !isDomesticTypeInitialized,
        refetchOnMountOrArgChange: true
    });

    // data önceki argümanın sonucunu tutar; eski sekmenin seçenekleri yeniden yazılmasın diye currentData kullanılır
    useEffect(() => {
        if (!filterOptionsLoaded && currentData?.filterOptions) {
            setFilterOptions(currentData.filterOptions);
            setFilterOptionsLoaded(true);
        }
    }, [currentData, filterOptionsLoaded]);

    const handleFilterChange = useCallback((updatedFilters) => {
        setActiveFilters((prev) => ({ ...prev, ...updatedFilters }));
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const initFilters = useCallback(() => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const handleSortChange = useCallback((event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;

        if (nextSortOrder !== 0 && !backendColumnMap[nextSortField]) return;

        setSortField(nextSortField);
        setSortOrder(nextSortOrder);
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const onPage = useCallback((event) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        });
    }, []);

    const handleSetActiveIndex = useCallback((index) => {
        try {
            localStorage.setItem('dealerPartsReturnProcessActiveTab', index.toString());
            setActiveIndex(index);
        } catch (error) {
            setActiveIndex(index);
        }
        setFilterOptions(null);
        setFilterOptionsLoaded(false);
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const handleDomesticTypeChange = useCallback(
        (value) => {
            const nextDomesticType = value ?? null;
            setDomesticType(nextDomesticType);
            // Fatura No kolonu Yurt İçi'nde gizli; görünmeyen kolona ait filtre/sıralama istekte kalmamalı
            if (nextDomesticType === DOMESTIC_TYPE.DOMESTIC) {
                setActiveFilters((prev) => ({ ...prev, invoiceNo: null, invoiceNoMatchMode: null }));
                if (sortField === 'invoiceNo') {
                    setSortField(null);
                    setSortOrder(0);
                }
            }
            setFilterOptions(null);
            setFilterOptionsLoaded(false);
            setLazyParams(INITIAL_LAZY_PARAMS);
        },
        [sortField]
    );

    const handleReturnRequestSubmit = useCallback(
        async (formData, files, hideDialog) => {
            try {
                console.log('📤 Submitting return request with files:', files);

                const requestData = {
                    partCode: formData.partCode,
                    uom: formData.uom,
                    selectedReasonType: formData.selectedReasonType,
                    returnDescription: formData.returnDescription,
                    lineDescription: formData.lineDescription,
                    isFaultyPart: formData.isFaultyPart,
                    quantity: formData.quantity,
                    files: files
                };

                if (isDomestic === false) {
                    requestData.invoiceNo = formData.invoiceNo?.trim() || '';
                }

                if (formData.isFaultyPart) {
                    requestData.vinNo = formData.chassisNo;
                    requestData.assemblyKm = formData.assemblyKm;
                    requestData.faultKm = formData.faultKm;
                    requestData.assemblyDate = formData.assemblyDate ? new Date(formData.assemblyDate.getTime() - formData.assemblyDate.getTimezoneOffset() * 60000).toISOString() : null;
                    requestData.faultDate = formData.faultDate ? new Date(formData.faultDate.getTime() - formData.faultDate.getTimezoneOffset() * 60000).toISOString() : null;
                }

                console.log('📦 Request data:', {
                    ...requestData,
                    files: requestData.files?.length ? `${requestData.files.length} file(s)` : 'No files'
                });

                const result = await createReturnRequest(requestData).unwrap();

                if (result.success) {
                    toastService.success(th('dealerPartsReturnProcess.api.createSuccess'));
                    refetch();
                    hideDialog?.();
                } else {
                    toastService.error(th('dealerPartsReturnProcess.api.createError'));
                }
            } catch (error) {
                console.error('❌ Return request creation failed:', error);
            }
        },
        [createReturnRequest, th, refetch, isDomestic]
    );

    const handleSendToRM = useCallback(
        async (returnRequestId, onSuccess) => {
            try {
                const result = await sendToRegionalManager(returnRequestId).unwrap();

                if (result.success) {
                    toastService.success(th('dealerPartsReturnProcess.api.sendToRmSuccess'));
                    refetch();
                    onSuccess?.();
                } else {
                    toastService.error(th('dealerPartsReturnProcess.api.sendToRmError'));
                }
            } catch (error) {
                console.error('❌ Send to RM failed:', error);
            }
        },
        [sendToRegionalManager, th, refetch]
    );

    const handleCancelRequest = useCallback(
        async (returnRequestId, onSuccess) => {
            try {
                const result = await cancelReturnRequest(returnRequestId).unwrap();

                if (result.success) {
                    toastService.success(th('dealerPartsReturnProcess.api.cancelSuccess'));
                    refetch();
                    onSuccess?.();
                } else {
                    toastService.error(th('dealerPartsReturnProcess.api.cancelError'));
                }
            } catch (error) {
                console.error('❌ Cancel return request failed:', error);
            }
        },
        [cancelReturnRequest, th, refetch]
    );

    const handleApproveRequest = useCallback(
        async (returnRequestId, onSuccess) => {
            try {
                const result = await approveReturnRequest(returnRequestId).unwrap();
                if (result.success) {
                    toastService.success(th('dealerPartsReturnProcess.api.approveSuccess'));
                    refetch();
                    onSuccess?.();
                } else {
                    toastService.error(th('dealerPartsReturnProcess.api.approveError'));
                }
            } catch (error) {
                console.error('❌ Approve request failed:', error);
            }
        },
        [approveReturnRequest, th, refetch]
    );

    const handleRejectRequest = useCallback(
        async (returnRequestId, rejectionReason, onSuccess) => {
            try {
                const result = await rejectReturnRequest({ id: returnRequestId, rejectionReason }).unwrap();
                if (result.success) {
                    toastService.success(th('dealerPartsReturnProcess.api.rejectSuccess'));
                    refetch();
                    onSuccess?.();
                } else {
                    toastService.error(th('dealerPartsReturnProcess.api.rejectError'));
                }
            } catch (error) {
                console.error('❌ Reject request failed:', error);
            }
        },
        [rejectReturnRequest, th, refetch]
    );

    const tabCounts = useMemo(() => apiData?.tabCounts ?? { draft: 0, sentForApproval: 0, approved: 0, rejected: 0 }, [apiData]);

    return {
        apiData,
        isDomestic,
        isLoading: isLoading || isFetching || !isDomesticTypeInitialized,
        isFetching,
        error,
        activeIndex,
        lazyParams,
        activeFilters,
        filterOptions,
        sortField,
        sortOrder,
        domesticType,
        handleFilterChange,
        initFilters,
        onPage,
        onSort: handleSortChange,
        handleDomesticTypeChange,
        refetch,
        setActiveIndex: handleSetActiveIndex,
        handleReturnRequestSubmit,
        isSubmitting,
        handleSendToRM,
        isSendingToRM,
        handleCancelRequest,
        isCancellingRequest,
        handleApproveRequest,
        isApproving,
        handleRejectRequest,
        isRejecting,
        exportParams,
        tabCounts
    };
};

export default useDealerPartsReturnProcessData;
